import { useMutation } from '@apollo/client'
import { SnackbarVariant } from '@focaldata/cin-ui-components'
import { SET_GLOBAL_AUDIENCE_MEMBER_V2 } from '../../data/gql/questionnaire/mutations/setGlobalAudienceMemberV2'
import { draftAudienceRefetchQuery } from '../../data/gql/questionnaire/queries/audiences'
import { LoggerErrorType } from '../../data/logger'
import { captureApolloError } from '../../utils/HelperFunctions'
import {
  GLOBAL_AUDIENCE_READ_ONLY_MESSAGE,
  isGlobalAudienceReadOnlyError
} from '../../utils/globalAudienceError'
import { useOptionalWaveId } from '../useOptionalWaveId'
import { useProjectId } from '../useProjectId'
import { useSafeEnqueueSnackbar } from '../useSafeEnqueueSnackbar'
import { useSurveyId } from '../useSurveyId'
import { evictAudienceCaches } from './evictAudienceCaches'

/**
 * Marks a custom audience member as global (or resets it). Only meaningful in
 * the base market — the wording/options of a global member become read-only in
 * every other market. `memberId` is the member's questionLk, matching the
 * sibling updateAudienceMemberV2 mutation on the same resolver.
 */
export const useSetGlobalAudienceMemberV2 = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const enqueueSnackbar = useSafeEnqueueSnackbar()

  const [setGlobalAudienceMemberV2Mutation] = useMutation(
    SET_GLOBAL_AUDIENCE_MEMBER_V2,
    {
      context: { clientName: 'questionnaire' },
      onError(error) {
        captureApolloError(
          LoggerErrorType.ApolloMutation,
          'setGlobalAudienceMemberV2',
          error
        )
        if (isGlobalAudienceReadOnlyError(error)) {
          enqueueSnackbar?.(GLOBAL_AUDIENCE_READ_ONLY_MESSAGE, {
            variant: SnackbarVariant.Error
          })
        } else {
          // Any other failure (network, server) would otherwise be silent —
          // give the user feedback so a failed toggle isn't invisible.
          enqueueSnackbar?.(
            'Could not update this question. Please try again.',
            {
              variant: SnackbarVariant.Error
            }
          )
        }
      },
      // The current market is refetched here; other markets are auto-updated by
      // backend propagation, so evict their (unmounted) audience caches.
      refetchQueries: [draftAudienceRefetchQuery(projectId, surveyId, waveId)],
      update: (cache) => evictAudienceCaches(cache)
    }
  )

  const setGlobalAudienceMember = (memberId: string, isGlobal: boolean) =>
    setGlobalAudienceMemberV2Mutation({
      variables: {
        input: {
          projectId,
          surveyId,
          waveId,
          memberId,
          isGlobal
        }
      }
    })

  return setGlobalAudienceMember
}
